import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Loader2 } from 'lucide-react';
import { getAllProviders } from '../../services/adminproviders.api';
import { Provider } from '../../types/pppadminproviders-model';

const AdminProviders: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadProviders = () => {
    setLoading(true);
    getAllProviders()
      .then(setProviders)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const filtered = providers.filter(
    p =>
      !search ||
      p.fullname.toLowerCase().includes(search.toLowerCase()) ||
      p.mobile.toLowerCase().includes(search.toLowerCase()) ||
      p.gst.toLowerCase().includes(search.toLowerCase())
  );

  const stats = [
    { l: 'Total', v: providers.length, c: 'text-amber-600' },
    { l: 'Verified', v: providers.filter(p => p.dflag === 0).length, c: 'text-emerald-600' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 px-8 py-7">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-stone-800">Provider Management</h1>
          <p className="mt-1 text-[13px] text-stone-500">
            Manage advertising space providers and KYC verifications
          </p>
        </div>
        <div className="flex gap-4">
          {stats.map(s => (
            <div key={s.l} className="text-center">
              <div className={`font-serif text-[22px] font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[11px] text-stone-400">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-5 rounded-2xl border border-stone-200 bg-white px-[18px] py-3.5 shadow-sm">
        <div className="relative w-80">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            placeholder="Search by name, mobile or GST..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-[10px] border border-stone-300 bg-white py-2 pl-9 pr-3 text-[13px] text-stone-700 placeholder:text-stone-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="bg-stone-100">
                {['#', 'Provider', 'Mobile', 'GST Number', 'Bank', 'KYC', 'Status', 'Joined', 'Actions'].map(h => (
                  <th
                    key={h}
                    className="whitespace-nowrap px-[18px] py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <Loader2 size={22} className="mx-auto animate-spin text-amber-600" />
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-[13px] text-rose-600">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-[13px] text-stone-400">
                    No providers found.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                filtered.map(p => (
                  <tr key={p.id} className="border-b border-stone-100 bg-white last:border-b-0 hover:bg-amber-50/40">
                    <td className="px-[18px] py-3.5 text-[12px] font-semibold text-stone-400">{p.sno}</td>
                    <td className="px-[18px] py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-600 text-[11px] font-bold text-white">
                          {p.fullname.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-semibold text-stone-800">{p.fullname}</div>
                          <div className="truncate text-[11px] text-stone-400">{p.address}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-[18px] py-3.5 font-mono text-[12px] text-stone-600">{p.mobile}</td>
                    <td className="px-[18px] py-3.5 font-mono text-[12px] text-stone-600">{p.gst}</td>
                    <td className="px-[18px] py-3.5">
                      <div className="text-[12px] font-medium text-stone-800">{p.bankname}</div>
                      <div className="font-mono text-[11px] text-stone-400">{p.ifsc}</div>
                    </td>
                    <td className="px-[18px] py-3.5 text-[11px] text-stone-400">View details</td>
                    <td className="px-[18px] py-3.5">
                      <span
                        className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                          p.dflag === 0
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-stone-200 bg-stone-100 text-stone-500'
                        }`}
                      >
                        {p.dflag === 0 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-[18px] py-3.5 text-[12px] text-stone-400">{p.edate}</td>
                    <td className="px-[18px] py-3.5">
                      <button
                        onClick={() => navigate(`/admin/providers/provider-details/${p.id}`)}
                        className="flex items-center gap-1 rounded-lg border border-stone-200 bg-stone-100 px-3 py-1.5 text-[11px] font-medium text-stone-600 transition hover:brightness-95"
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminProviders;